import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Checkbox, Form, Input, Modal } from 'antd';
import { Clapperboard, Film, Infinity as InfinityIcon, RectangleHorizontal, RectangleVertical, Timer, } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreationEntry } from '@/features/create-project/ui/CreationEntry';
import { CreationPlanPicker } from '@/features/create-project/ui/CreationPlanPicker';
import { readCreationSession, releaseCreationSession, } from '@/features/create-project/model/creationSession';
import { useQueryClient } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { PROJECT_LIST_QUERY_KEY, createProject, updateProject, submitProjectDraft, uploadProjectScript, configureProjectCreation, fetchProjectCreationFlow, runProjectCreationTask, } from '@/api/projectApi';
import { createEmptyProjectDocument } from '@/entities/project/model/projectDocument';
import { PROJECT_DURATION_TIER_OPTIONS, PROJECT_INTERACTION_LEVEL_OPTIONS, PROJECT_SCREEN_RATIO_OPTIONS, PROJECT_TYPE_OPTIONS, } from '@/entities/project/model/projectMeta';
import { uploadProjectCover } from '@/features/create-project/api/projectCoverApi';
import { closeCreateProjectDialog } from '@/features/create-project/model/createProjectSlice';
import { ArtStylePicker } from '@/features/create-project/ui/ArtStylePicker';
import { ProjectCoverField } from '@/shared/ui/ProjectCoverField/ProjectCoverField';
import { ProjectOptionCards, } from '@/features/create-project/ui/ProjectOptionCards';
import { ProjectOptionList } from '@/features/create-project/ui/ProjectOptionList';
import { notifyApiError, notifyApiSuccess, notifyApiWarning } from '@/shared/api/httpClient';
import '@/features/create-project/ui/styles/create-project-dialog.css';
/** 项目名称最大长度（字）。 */
const PROJECT_NAME_MAX_LENGTH = 15;
/** 项目简介最大长度（字）。 */
const PROJECT_DESCRIPTION_MAX_LENGTH = 200;
/** 弹窗宽度（px），对齐 UI 规范 6.11 的 Large 弹窗尺寸。 */
const CREATE_PROJECT_DIALOG_WIDTH = 880;
/** 互动程度默认编码：标准改编。字段暂时隐藏时仍以该默认值提交，保证校验与落库不受影响。 */
const DEFAULT_INTERACTION_LEVEL = 'standard';
/** 预计篇幅选项图示，键为篇幅编码。 */
const DURATION_TIER_ICONS = {
    short: _jsx(Timer, { "aria-hidden": "true", size: 18 }),
    medium: _jsx(Clapperboard, { "aria-hidden": "true", size: 18 }),
    long: _jsx(Film, { "aria-hidden": "true", size: 18 }),
    unlimited: _jsx(InfinityIcon, { "aria-hidden": "true", size: 18 }),
};
/** 画面比例卡片图示，键为比例标识。 */
const SCREEN_RATIO_ICONS = {
    '16:9': _jsx(RectangleHorizontal, { "aria-hidden": "true", size: 18 }),
    '9:16': _jsx(RectangleVertical, { "aria-hidden": "true", size: 18 }),
};
/** 预计篇幅卡片选项，图示取自 DURATION_TIER_ICONS。 */
const DURATION_TIER_CARDS = PROJECT_DURATION_TIER_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    description: option.description,
    icon: DURATION_TIER_ICONS[option.value],
}));
/** 画面比例卡片选项，图示取自 SCREEN_RATIO_ICONS。 */
const SCREEN_RATIO_CARDS = PROJECT_SCREEN_RATIO_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    description: option.description,
    icon: SCREEN_RATIO_ICONS[option.value],
}));
/**
 * 解析提交用的封面地址：本地新选的封面（dataURL）在提交时才经上传素材接口转存 OSS；
 * 与项目已保存封面一致的值直接透传（历史项目也可能存的是 dataURL），避免重复上传；
 * 上传失败时回退提交 dataURL 由后端兜底转存，保证上传异常不会阻断项目创建/保存；
 * 未设置封面时返回 undefined，让后端清空该字段并清理旧 OSS 文件。
 * @param cover 表单中的封面值，可能为空、dataURL 或已保存的 OSS URL。
 * @param savedCoverUrl 项目已保存的封面地址，创建模式为 undefined。
 * @returns 传给创建/更新接口的封面地址。
 */
async function resolveCoverUrl(cover, savedCoverUrl) {
    if (!cover)
        return undefined;
    if (cover === savedCoverUrl || !cover.startsWith('data:'))
        return cover;
    try {
        return await uploadProjectCover(cover);
    }
    catch (error) {
        // 上传异常不阻断主流程：后端 storeImage 会把 dataURL 兜底转存 OSS。
        console.warn('[CreateProjectForm] 封面上传失败，回退提交 dataURL:', error);
        return cover;
    }
}
/**
 * 查询历史数据中的画面比例编码是否仍属于当前可选项。
 * 非可选项（如更早期由编辑器写入的其他比例）返回 undefined，由创作者重新选择。
 * @param code 项目记录中的画面比例编码。
 * @returns 命中的可选项编码，未命中时返回 undefined。
 */
function resolveScreenRatio(code) {
    return PROJECT_SCREEN_RATIO_OPTIONS.find((option) => option.value === code)?.value;
}
/**
 * 归一化游戏变量：仅去除名称首尾空白，空行由表单校验统一拦截。
 * @param variables 表单中的变量列表。
 * @returns 可直接提交的变量列表。
 */
function normalizeGameVariables(variables) {
    return (variables ?? []).map((variable) => ({
        name: variable.name.trim(),
        initialValue: variable.initialValue,
    }));
}
/**
 * 构造字段标签节点：默认按必填渲染字段名后的红色星号，非必填字段显式传入 optional 关闭；
 * 附加说明（如「选填」）依次排在星号之后，避免与字段名混淆。
 * 星号图形由样式层用 ::before 内容绘制，不进入标签文本，
 * 既保持标签可访问名称仍为纯字段名，也避免与必填控件的 aria-required 重复播报。
 * @param name 字段名称。
 * @param options optional 为是否非必填，hint 为附加说明文案。
 * @returns 表单标签节点。
 */
function buildFieldLabel(name, options) {
    /** 是否渲染必填星号：仅显式声明非必填时不渲染。 */
    const isRequired = options?.optional !== true;
    return (_jsxs("span", { className: "create-project-form__label-row", children: [_jsxs("span", { className: "create-project-form__label-name", children: [_jsx("span", { children: name }), isRequired ? (_jsx("span", { className: "create-project-form__label-required", "aria-hidden": "true" })) : null] }), options?.hint ? (_jsx("span", { className: "create-project-form__label-hint", children: options.hint })) : null] }));
}
/** 渲染创建/编辑项目表单，随弹窗关闭销毁；编辑模式携带项目初始值。 */
function CreateProjectForm(props) {
    const { project } = props;
    const isEdit = project !== null;
    const ownerId = useAppSelector((state) => state.session.user?.userId);
    const session = ownerId === undefined ? undefined : readCreationSession(props.sessionKey, ownerId);
    const navigate = useNavigate();
    const sessionRef = useRef(session);
    const [selectedPlanId, setSelectedPlanId] = useState(session?.selectedPlanId ?? null);
    const submittingRef = useRef(false);
    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector((state) => state.session.status === 'authenticated');
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    /** 提交中标记，驱动创建按钮 loading 并防止重复提交。 */
    const [submitting, setSubmitting] = useState(false);
    /**
     * 编辑模式下回填到表单的画面比例初始值。
     * 历史项目可能没有该值（列新增或早期由编辑器写入的比例已不在候选内），
     * 此时保持未选中，让创作者在编辑弹窗内补齐一次。
     */
    const initialScreenRatio = project ? resolveScreenRatio(project.screenRatio) : undefined;
    /**
     * 表单提交：创建模式调用创建接口落库，编辑模式调用更新接口；
     * 本地新选的封面在此刻才上传 OSS，成功后关闭弹窗，失败时保留弹窗与已填内容并提示错误。
     * @param values 已通过校验的表单值。
     */
    async function handleFinish(values) {
        const session = sessionRef.current;
        // 创建项目必须依赖登录会话，拦截绕过入口直接提交的情况。
        if (!isEdit && !isAuthenticated) {
            notifyApiWarning('您还没有登录，请先登录');
            return;
        }
        if (submittingRef.current)
            return;
        if (!isEdit && session && session.ownerId !== ownerId) {
            notifyApiWarning('登录信息已变化，请重新开始创作');
            return;
        }
        if (!isEdit && (!session || (session.mode === 'inspiration' && selectedPlanId === null))) {
            notifyApiWarning('请先选择已生成成功的剧情方案');
            return;
        }
        submittingRef.current = true;
        setSubmitting(true);
        try {
            const payload = {
                name: values.name.trim(),
                types: values.types,
                artStyle: values.artStyle,
                durationTier: values.durationTier,
                screenRatio: values.screenRatio,
                interactionLevel: values.interactionLevel,
                /**
                 * 数值系统已从弹窗下线：表单不再收集变量，编辑模式沿用该项目已有的一份
                 * （否则保存会把项目变量清空），新建项目按空列表提交。
                 */
                gameVariables: normalizeGameVariables(values.gameVariables ?? project?.gameVariables ?? undefined),
                cover: await resolveCoverUrl(values.cover, project?.coverUrl ?? undefined),
                description: values.description?.trim() ?? undefined,
            };
            if (isEdit) {
                await updateProject({ ...payload, id: project.id });
                notifyApiSuccess('项目已保存');
            }
            else {
                // 画面比例只写入项目表 screen_ratio（由 payload.screenRatio 承载），不再冗余写进文档设置。
                const document = createEmptyProjectDocument({
                    name: payload.name,
                    description: payload.description,
                });
                if (!session)
                    throw new Error('创建会话已失效，请重新选择入口');
                let flow;
                if (session.mode === 'inspiration') {
                    if (!session.draftId || selectedPlanId === null)
                        throw new Error('请选择剧情方案');
                    flow = await submitProjectDraft({
                        ...payload,
                        document,
                        projectId: session.draftId,
                        planId: selectedPlanId,
                    });
                    session.projectId = flow.projectId;
                    // 草稿转正响应丢失或发起失败后，重试也保存用户修改过的最新配置。
                    await updateProject({ ...payload, id: session.projectId });
                }
                else {
                    if (!session.projectId) {
                        const created = await createProject({
                            ...payload,
                            document,
                            requestKey: session.requestKey,
                        });
                        session.projectId = created.id;
                        // 首次响应丢失时创建接口返回原项目，仍提交本次最新配置。
                        await updateProject({ ...payload, id: session.projectId });
                    }
                    else {
                        // 上传失败后保留项目与已上传文件，重试不创建第二个项目。
                        await updateProject({ ...payload, id: session.projectId });
                    }
                    for (let index = 0; index < session.files.length; index++) {
                        if (session.uploadedFiles.has(index))
                            continue;
                        const file = session.files[index];
                        await uploadProjectScript(session.projectId, file, session.mode === 'non_standard' || session.mode === 'standard'
                            ? session.mode
                            : undefined);
                        session.uploadedFiles.add(index);
                    }
                    // 发起失败（例如积分不足）后复用已有待执行任务，不重新配置或重复计费。
                    flow = session.configured
                        ? await fetchProjectCreationFlow(session.projectId)
                        : await configureProjectCreation({
                            projectId: session.projectId,
                            creationMode: session.mode,
                            novelGenerationStrategy: session.novelGenerationStrategy,
                        });
                    session.configured = true;
                }
                const taskType = session.mode === 'inspiration'
                    ? 'inspiration_script'
                    : session.mode === 'non_standard'
                        ? 'non_standard_to_standard'
                        : 'standard_script_split';
                const task = [...flow.tasks]
                    .sort((a, b) => b.id - a.id)
                    .find((item) => item.taskType === taskType);
                if (task && (task.status === 0 || task.status === 3))
                    await runProjectCreationTask(task.id);
                notifyApiSuccess(session.mode === 'blank' ? '项目创建成功' : '已开始生成剧本');
                void navigate(`/projects/${String(session.projectId)}`);
                if (props.sessionKey)
                    releaseCreationSession(props.sessionKey);
            }
            dispatch(closeCreateProjectDialog(true));
            // 成功后刷新项目列表，保证「我的项目」页实时可见。
            void queryClient.invalidateQueries({ queryKey: PROJECT_LIST_QUERY_KEY });
        }
        catch (error) {
            notifyApiError(error, {
                fallback: isEdit ? '保存项目失败，请稍后重试' : '创建项目失败，请稍后重试',
            });
        }
        finally {
            if (session?.projectId)
                void queryClient.invalidateQueries({ queryKey: PROJECT_LIST_QUERY_KEY });
            submittingRef.current = false;
            setSubmitting(false);
        }
    }
    return (_jsxs(Form, { className: "create-project-form", form: form, layout: "vertical", requiredMark: false, initialValues: isEdit
            ? {
                name: project.name,
                types: project.types ?? [],
                artStyle: project.artStyle ?? undefined,
                durationTier: project.durationTier ?? undefined,
                screenRatio: initialScreenRatio,
                interactionLevel: project.interactionLevel ?? DEFAULT_INTERACTION_LEVEL,
                cover: project.coverUrl ?? undefined,
                description: project.description ?? undefined,
            }
            : {
                types: [],
                interactionLevel: DEFAULT_INTERACTION_LEVEL,
                ...session?.formValues,
            }, onValuesChange: (_changed, values) => {
            const currentSession = sessionRef.current;
            if (currentSession)
                currentSession.formValues = values;
        }, onFinish: (values) => {
            void handleFinish(values);
        }, children: [_jsxs("div", { className: "create-project-form__scroll", children: [!isEdit && session?.draftId ? (_jsx(CreationPlanPicker, { projectId: session.draftId, autoStartTaskId: session.autoStartPlanTaskId, onAutoStart: () => {
                            const currentSession = sessionRef.current;
                            if (currentSession)
                                currentSession.autoStartPlanTaskId = undefined;
                        }, inspiration: session.inspiration ?? '', value: selectedPlanId, onChange: (id) => {
                            setSelectedPlanId(id);
                            const currentSession = sessionRef.current;
                            if (currentSession)
                                currentSession.selectedPlanId = id;
                        } })) : null, !isEdit && session && session.mode !== 'blank' && (_jsx("p", { children: session.mode === 'inspiration'
                            ? `灵感：${session.inspiration ?? ''}`
                            : `已选择 ${String(session.files.length)} 个文件：${session.files.map((file) => file.name).join('、')}` })), _jsx(Form.Item, { label: buildFieldLabel('项目名称'), name: "name", rules: [
                            { required: true, message: '请输入项目名称' },
                            {
                                max: PROJECT_NAME_MAX_LENGTH,
                                message: `项目名称最多 ${String(PROJECT_NAME_MAX_LENGTH)} 个字`,
                            },
                        ], children: _jsx(Input, { placeholder: `最多 ${String(PROJECT_NAME_MAX_LENGTH)} 个字`, maxLength: PROJECT_NAME_MAX_LENGTH, showCount: true }) }), _jsx(Form.Item, { label: buildFieldLabel('项目封面'), name: "cover", rules: [{ required: true, message: '请上传项目封面' }], children: _jsx(ProjectCoverField, { onError: (errorMessage) => {
                                notifyApiError(errorMessage);
                            } }) }), _jsx(Form.Item, { label: buildFieldLabel('项目简介'), name: "description", rules: [{ required: true, whitespace: true, message: '请输入项目简介' }], children: _jsx(Input.TextArea, { placeholder: "\u7B80\u5355\u4ECB\u7ECD\u4E00\u4E0B\u8FD9\u4E2A\u5F71\u6E38", maxLength: PROJECT_DESCRIPTION_MAX_LENGTH, showCount: true, autoSize: { minRows: 3, maxRows: 5 } }) }), _jsx(Form.Item, { label: buildFieldLabel('画面风格'), name: "artStyle", rules: [{ required: true, message: '请选择画面风格' }], children: _jsx(ArtStylePicker, {}) }), _jsx(Form.Item, { label: buildFieldLabel('预计篇幅'), name: "durationTier", rules: [{ required: true, message: '请选择预计篇幅' }], children: _jsx(ProjectOptionCards, { options: DURATION_TIER_CARDS, ariaLabel: "\u9884\u8BA1\u7BC7\u5E45" }) }), _jsx(Form.Item, { label: buildFieldLabel('画面比例'), name: "screenRatio", rules: [{ required: true, message: '请选择画面比例' }], children: _jsx(ProjectOptionCards, { options: SCREEN_RATIO_CARDS, ariaLabel: "\u753B\u9762\u6BD4\u4F8B" }) }), _jsx(Form.Item, { label: buildFieldLabel('题材类型'), name: "types", rules: [{ required: true, message: '请选择题材类型' }], children: _jsx(Checkbox.Group, { className: "create-project-form__choice-group", options: [...PROJECT_TYPE_OPTIONS] }) }), _jsx(Form.Item, { label: buildFieldLabel('互动程度'), name: "interactionLevel", hidden: true, rules: [{ required: true, message: '请选择互动程度' }], children: _jsx(ProjectOptionList, { options: PROJECT_INTERACTION_LEVEL_OPTIONS, ariaLabel: "\u4E92\u52A8\u7A0B\u5EA6" }) })] }), _jsxs("div", { className: "create-project-form__actions", children: [_jsx(Button, { className: "create-project-form__cancel", type: "default", onClick: () => dispatch(closeCreateProjectDialog()), children: "\u53D6\u6D88" }), _jsx(Button, { className: "create-project-form__submit", type: "primary", htmlType: "submit", loading: submitting, children: isEdit
                            ? '保存更改'
                            : session?.mode === 'blank'
                                ? '创建项目'
                                : session?.mode === 'standard'
                                    ? '解析剧本 · 1 积分'
                                    : '生成剧本 · 1 积分' })] })] }));
}
/** 渲染创建/编辑项目弹窗，由 Redux 状态驱动显隐与编辑目标。 */
export function CreateProjectDialog() {
    const dispatch = useAppDispatch();
    const { isDialogOpen, editingProject, sessionKey } = useAppSelector((state) => state.createProject);
    return (_jsx(Modal, { className: "create-project-dialog", rootClassName: "create-project-dialog-root", title: editingProject ? '编辑项目' : '创建项目', width: CREATE_PROJECT_DIALOG_WIDTH, centered: true, open: isDialogOpen, footer: null, destroyOnHidden: true, onCancel: () => dispatch(closeCreateProjectDialog()), children: isDialogOpen ? (editingProject || sessionKey ? (_jsx(CreateProjectForm, { project: editingProject, sessionKey: sessionKey }, sessionKey ?? String(editingProject?.id))) : (
        /* 兜底：正常入口都会携带创建会话键（点击创建项目即按「新建项目」建立会话），
           仅在无会话的异常路径下退回选择入口。 */
        _jsx(CreationEntry, {}))) : null }));
}
